import { Alert, Button, Input, Stack, Text } from '@mantine/core';
import {
  useEditingVersionSnapshot,
  useEditVersionContext,
} from '@/context/EditVersionContext/useEditVersionContext.ts';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import StyledInputErrorText from '@/components/StyledInputFields/StyledInputErrorText.tsx';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import SingleDataInputSelectorInner from '@/pages/Editor/components/SharedFields/DataInputSelector/SingleDataInputSelectorInner.tsx';
import OutputNameField from '@/pages/Editor/components/SharedFields/OutputNameField.tsx';
import StepAdvancedConfigFields from '@/pages/Editor/components/SharedFields/StepAdvancedConfigFields.tsx';
import StepCommonFields from '@/pages/Editor/components/SharedFields/StepCommonFields.tsx';
import useRetrieveAutomations from '@/hooks/api/automations/useRetrieveAutomations.ts';
import useCompositionOperations from '@/hooks/api/automations/useCompositionOperations.ts';
import useRetrieveAutomationVersion from '@/hooks/api/automationVersions/useRetrieveAutomationVersion.ts';
import useRetrieveAutomationVersionDependencies from '@/hooks/api/automationVersions/useRetrieveAutomationVersionDependencies.ts';
import useRetrieveAutomationVersions from '@/hooks/api/automationVersions/useRetrieveAutomationVersions.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import { getStepErrors, StepErrorType } from '@/types/stepErrorTypes.ts';
import { OutputAvailability, StepType } from '@/types/automationFields.ts';
import {
  SubAutomationExecutionMode,
  SubAutomationStep,
  PinnedSubAutomationStep,
  LogicalSubAutomationStep,
  SubAutomationTriggerInputMapping,
  TriggerStep,
  TriggerType,
} from '@/types/automationSteps.ts';
import {
  buildSubAutomationVersionOptions,
  buildSubAutomationTargetOptions,
  syncSubAutomationTriggerInputMappings,
} from './subAutomationFormUtils.ts';

type Fields = {
  execution_mode: SubAutomationExecutionMode;
  target_automation_id: string | null;
  target_automation_version_id: string | null;
  target_trigger_step_id: string | null;
  trigger_input_mappings: SubAutomationTriggerInputMapping[];
  target_manual_trigger_inputs: PinnedSubAutomationStep['target_manual_trigger_inputs'];
  target_pinned_outputs: PinnedSubAutomationStep['target_pinned_outputs'];
};

const executionModeOptions = [
  { value: SubAutomationExecutionMode.Blocking, label: 'Blocking' },
  { value: SubAutomationExecutionMode.Async, label: 'Async' },
];

function getIndexedMappingErrors(step: SubAutomationStep) {
  return new Map(
    step.errors
      .filter((error) => error.type === StepErrorType.InvalidSubAutomationInputMapping)
      .filter((error) => error.index !== null)
      .map((error) => [error.index!, error.message]),
  );
}

function getPinnedArtifactSummary(
  contract: PinnedSubAutomationStep['target_pinned_outputs'][number]['output_contract'],
) {
  const transportLabel =
    contract.original_file_transport === 'runtime_checked'
      ? 'Original file · checked at runtime'
      : null;
  if (contract.artifact_kind !== 'immutable_file') return transportLabel;
  const artifactLabel =
    contract.artifact_evidence_profile === 'workbook' ? 'Workbook artifact' : 'File artifact';
  const versionLabel = contract.version ? ` · contract v${contract.version}` : '';
  const mimeTypesLabel = contract.expected_mime_types?.length
    ? ` · MIME: ${contract.expected_mime_types.join(', ')}`
    : '';
  return `${artifactLabel} · ${contract.dataset_type}${versionLabel}${mimeTypesLabel}${transportLabel ? ` · ${transportLabel}` : ''}`;
}

const SubAutomationTargetFields = ({ step }: { step: PinnedSubAutomationStep }) => {
  const navigate = useNavigate();
  const currentAutomationId = useAutomationId();
  const { data: automations } = useRetrieveAutomations();
  const { fields } = useUpdateStepFields<Fields>({ step, syncWithAPI: true });
  const { mutate: applyCompositionOperation, isPending: isApplyingComposition } =
    useCompositionOperations();
  const [selectedTargetAutomationId, setSelectedTargetAutomationId] = useState<string | null>(
    step.target_automation_id,
  );
  const [selectedTargetVersionId, setSelectedTargetVersionId] = useState<string | null>(
    step.target_automation_version_id,
  );
  const [pendingMappings, setPendingMappings] = useState<SubAutomationTriggerInputMapping[]>(
    step.trigger_input_mappings,
  );
  const [pendingExecutionMode, setPendingExecutionMode] = useState<SubAutomationExecutionMode>(
    step.execution_mode,
  );
  const {
    data: draftTargetVersions,
    fetchNextPage: fetchNextDraftPage,
    hasNextPage: hasNextDraftPage,
    isFetchingNextPage: isFetchingNextDraftPage,
  } = useRetrieveAutomationVersions(false, selectedTargetAutomationId ?? undefined);
  const {
    data: publishedTargetVersions,
    fetchNextPage: fetchNextPublishedPage,
    hasNextPage: hasNextPublishedPage,
    isFetchingNextPage: isFetchingNextPublishedPage,
  } = useRetrieveAutomationVersions(true, selectedTargetAutomationId ?? undefined);
  const { data: selectedTargetVersion } = useRetrieveAutomationVersion(
    selectedTargetVersionId ?? undefined,
  );
  const { data: selectedTargetDependencies } = useRetrieveAutomationVersionDependencies(
    selectedTargetVersionId ?? undefined,
  );

  const targetOptions = useMemo(
    () =>
      buildSubAutomationTargetOptions(
        automations?.results,
        currentAutomationId,
        step.target_automation_id,
      ),
    [automations?.results, currentAutomationId, step.target_automation_id],
  );
  const selectedTargetRetired = useMemo(
    () =>
      automations?.results.find((automation) => automation.id === selectedTargetAutomationId)
        ?.retired_at != null,
    [automations?.results, selectedTargetAutomationId],
  );
  const canOpenSelectedTarget = useMemo(
    () =>
      !!selectedTargetAutomationId &&
      (automations?.results ?? []).some(
        (automation) => automation.id === selectedTargetAutomationId,
      ),
    [automations?.results, selectedTargetAutomationId],
  );
  const indexedMappingErrors = useMemo(() => getIndexedMappingErrors(step), [step]);
  const versionOptions = useMemo(
    () =>
      buildSubAutomationVersionOptions(
        [
          ...(draftTargetVersions?.pages ?? []).flatMap((page) =>
            page.results.map((version) => ({ ...version, isPublished: false })),
          ),
          ...(publishedTargetVersions?.pages ?? []).flatMap((page) =>
            page.results.map((version) => ({ ...version, isPublished: true })),
          ),
        ],
        step.target_automation_version_id,
        selectedTargetVersion,
      ),
    [
      draftTargetVersions?.pages,
      publishedTargetVersions?.pages,
      selectedTargetVersion,
      step.target_automation_version_id,
    ],
  );

  useEffect(() => {
    setSelectedTargetAutomationId(step.target_automation_id);
    setSelectedTargetVersionId(step.target_automation_version_id);
    setPendingMappings(step.trigger_input_mappings);
    setPendingExecutionMode(step.execution_mode);
  }, [step]);

  const selectedManualTriggerInputs = useMemo(() => {
    const trigger = selectedTargetVersion?.steps.find(
      (candidate): candidate is TriggerStep =>
        candidate.step_type === StepType.Trigger && candidate.trigger_type === TriggerType.Manual,
    );
    if (trigger) return trigger.manual_trigger_inputs;
    if (selectedTargetVersionId === step.target_automation_version_id) {
      return fields.target_manual_trigger_inputs;
    }
    return [];
  }, [
    fields.target_manual_trigger_inputs,
    selectedTargetVersion,
    selectedTargetVersionId,
    step.target_automation_version_id,
  ]);

  useEffect(() => {
    if (!selectedTargetVersion || selectedTargetVersion.id !== selectedTargetVersionId) return;
    setPendingMappings((current) =>
      syncSubAutomationTriggerInputMappings(selectedManualTriggerInputs, current),
    );
  }, [selectedManualTriggerInputs, selectedTargetVersion, selectedTargetVersionId]);

  const selectedVersionReady =
    !!selectedTargetVersion && selectedTargetVersion.id === selectedTargetVersionId;

  const targetError = useMemo(() => {
    return getStepErrors(step, [
      StepErrorType.InvalidSubAutomationTarget,
      StepErrorType.InvalidSubAutomationCycle,
    ])
      .map((error) => error.message)
      .join(', ');
  }, [step]);

  const generalMappingError = useMemo(() => {
    return step.errors
      .filter((error) => error.type === StepErrorType.InvalidSubAutomationInputMapping)
      .filter((error) => error.index === null)
      .map((error) => error.message)
      .join(', ');
  }, [step.errors]);

  return (
    <Stack gap="md">
      <StyledSelect
        label="Target automation"
        placeholder="Select an automation"
        data={targetOptions}
        value={selectedTargetAutomationId}
        onChange={(value) => {
          setSelectedTargetAutomationId(value);
          setSelectedTargetVersionId(null);
        }}
        searchable={true}
        clearable={true}
        error={targetError || undefined}
      />

      {selectedTargetAutomationId && (
        <Stack gap="xs">
          {selectedTargetRetired && (
            <Alert icon={<IconAlertTriangle size={16} />} color="orange">
              This parent is pinned to a retired child. The historical pin remains readable, but it
              cannot start a new child run. Select and apply a different child version only after
              review.
            </Alert>
          )}
          <StyledSelect
            label="Exact child version"
            placeholder="Select a draft or published version"
            data={versionOptions}
            value={selectedTargetVersionId}
            onChange={setSelectedTargetVersionId}
            searchable={true}
            clearable={false}
          />
          {(hasNextDraftPage || hasNextPublishedPage) && (
            <Button
              size="xs"
              variant="subtle"
              loading={isFetchingNextDraftPage || isFetchingNextPublishedPage}
              onClick={() => {
                if (hasNextDraftPage) void fetchNextDraftPage();
                if (hasNextPublishedPage) void fetchNextPublishedPage();
              }}
            >
              Load older versions
            </Button>
          )}
          <Text size="xs" c="dimmed">
            A child version is pinned exactly. Safe Builder tests may use a draft child;
            customer-facing runs require published children.
          </Text>
          {selectedTargetVersion && (
            <Text size="xs" c="dimmed">
              Reviewing v{selectedTargetVersion.version_number ?? '?'}
              {selectedTargetVersion.published_at
                ? ` · published ${new Date(selectedTargetVersion.published_at).toLocaleDateString()}`
                : ''}
              {selectedTargetVersionId === step.target_automation_version_id &&
              step.target_interface_fingerprint
                ? ' · interface evidence recorded'
                : ''}
            </Text>
          )}
          {selectedTargetDependencies && (
            <Text size="xs" c="dimmed">
              {selectedTargetDependencies.parents.length === 0
                ? 'No other parent automations currently pin this child version.'
                : `${selectedTargetDependencies.parents.length} parent automation${
                    selectedTargetDependencies.parents.length === 1 ? '' : 's'
                  } currently pin this child version.`}
            </Text>
          )}
          <StyledButton
            size="sm"
            variant="outline"
            loading={isApplyingComposition}
            disabled={
              !selectedTargetVersionId ||
              !selectedTargetAutomationId ||
              selectedTargetRetired ||
              !selectedVersionReady
            }
            onClick={() => {
              if (!selectedTargetAutomationId || !selectedTargetVersionId) return;
              applyCompositionOperation([
                {
                  op_type: 'replace_subautomation_target',
                  step_id: step.step_id,
                  value: {
                    target_automation_id: selectedTargetAutomationId,
                    target_automation_version_id: selectedTargetVersionId,
                    trigger_input_mappings: pendingMappings,
                    execution_mode: pendingExecutionMode,
                  },
                },
              ]);
            }}
          >
            {selectedTargetVersionId === step.target_automation_version_id
              ? 'Re-apply selected child version'
              : 'Apply child version'}
          </StyledButton>
          {canOpenSelectedTarget && (
            <StyledButton
              size="sm"
              variant="subtle"
              onClick={() => navigate(`/automations/${selectedTargetAutomationId}/editor`)}
            >
              Open accessible child automation
            </StyledButton>
          )}
        </Stack>
      )}

      <Input.Wrapper label="Execution mode">
        <StyledSelect
          data={executionModeOptions}
          value={pendingExecutionMode}
          onChange={(value) => setPendingExecutionMode(value as SubAutomationExecutionMode)}
          allowDeselect={false}
          clearable={false}
        />
      </Input.Wrapper>

      {selectedTargetAutomationId && (
        <Stack gap="sm">
          <Text size="sm" fw={500}>
            Child manual trigger inputs
          </Text>

          {!selectedTargetVersion &&
            selectedTargetVersionId !== step.target_automation_version_id && (
              <Text size="sm" c="dimmed">
                Loading the selected child interface…
              </Text>
            )}

          {selectedManualTriggerInputs.length === 0 && selectedTargetVersion && (
            <Text size="sm" c="dimmed">
              This automation&apos;s selected manual trigger does not require any inputs.
            </Text>
          )}

          {selectedManualTriggerInputs.map((manualTriggerInput, index) => {
            const mapping = pendingMappings[index];
            if (!mapping) return null;

            return (
              <Stack key={manualTriggerInput.output_id} gap="xs">
                <SingleDataInputSelectorInner
                  step={step}
                  label={manualTriggerInput.name}
                  dataInput={mapping.data_input}
                  dataInputError={indexedMappingErrors.get(index)}
                  onChange={(dataInput) => {
                    const nextMappings = [...pendingMappings];
                    nextMappings[index] = { ...mapping, data_input: dataInput };
                    setPendingMappings(nextMappings);
                  }}
                />
                {!!manualTriggerInput.description && (
                  <Text size="xs" c="dimmed" mt="-6px">
                    {manualTriggerInput.description}
                  </Text>
                )}
              </Stack>
            );
          })}

          {generalMappingError && (
            <StyledInputErrorText>{generalMappingError}</StyledInputErrorText>
          )}
        </Stack>
      )}

      {pendingExecutionMode === SubAutomationExecutionMode.Async ? (
        <OutputNameField step={step} />
      ) : selectedTargetAutomationId ? (
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            {selectedTargetVersionId === step.target_automation_version_id
              ? 'Current pinned outputs'
              : 'Selected version outputs'}
          </Text>
          {selectedTargetVersionId !== step.target_automation_version_id ? (
            <Text size="sm" c="dimmed">
              Output exposure will be derived and validated from the selected child version when you
              apply this pin.
            </Text>
          ) : fields.target_pinned_outputs.length === 0 ? (
            <Text size="sm" c="dimmed">
              Blocking mode only exposes outputs from pinned steps in the child automation.
            </Text>
          ) : (
            fields.target_pinned_outputs.map((output) => {
              const artifactSummary = getPinnedArtifactSummary(output.output_contract);
              return (
                <Text key={output.exposed_output_id} size="sm" c="dimmed">
                  {`${output.source_output_name || output.source_step_name} (${output.exposed_output_id})`}
                  {artifactSummary ? ` · ${artifactSummary}` : ''}
                  {output.output_contract.availability === OutputAvailability.RuntimeOptional
                    ? ' · May be unavailable'
                    : ''}
                </Text>
              );
            })
          )}
        </Stack>
      ) : null}
    </Stack>
  );
};

export function SubAutomationForm({ step }: { step: SubAutomationStep }) {
  if (step.binding_mode === 'composition') return <LogicalCallForm step={step} />;
  return <PinnedCallForm step={step} />;
}

function LogicalCallForm({ step }: { step: LogicalSubAutomationStep }) {
  const version = useEditingVersionSnapshot();
  const { hasPendingEdits, staleDraft } = useEditVersionContext();
  const navigate = useNavigate();
  const automationId = useAutomationId();
  return (
    <Stack>
      <Text fw={600}>Composition-bound call</Text>
      <Text>
        Target {step.target_automation_id} · trigger {step.target_trigger_step_id}
      </Text>
      <Text size="sm">
        The selected Release supplies this call’s exact implementation. There is no saved version
        pin on this declaration.
      </Text>
      {step.expected_interface?.outputs.map((port) => (
        <Text key={port.port_id} size="sm">
          {port.port_id} · {port.dataset_type} · {port.availability}
        </Text>
      ))}
      <Button
        disabled={!version || hasPendingEdits || Boolean(staleDraft)}
        onClick={() =>
          navigate(
            `/automations/${automationId}/release?version=${encodeURIComponent(version!.id)}`,
          )
        }
      >
        Review composition and logical contract
      </Button>
    </Stack>
  );
}

function PinnedCallForm({ step }: { step: PinnedSubAutomationStep }) {
  const { target } = useActiveCompositionTarget();
  const { fields, onChangeNoDelay } = useUpdateStepFields<Fields>({ step, syncWithAPI: true });
  const isModuleStep = target.scope_kind === 'local_subflow';

  if (isModuleStep) {
    return (
      <Stack gap="md">
        <StepCommonFields step={step} nameLabel="Step Name" />
        <Alert color="gray" title="Child automation binding">
          The pinned child automation, interface mappings, and output exposure are managed outside
          this module. You can adjust only this module step&apos;s execution mode here.
        </Alert>
        <Input.Wrapper label="Execution mode">
          <StyledSelect
            data={executionModeOptions}
            value={fields.execution_mode}
            onChange={(value) =>
              onChangeNoDelay({ execution_mode: value as SubAutomationExecutionMode })
            }
            allowDeselect={false}
            clearable={false}
          />
        </Input.Wrapper>
        <StepAdvancedConfigFields step={step} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <StepCommonFields step={step} nameLabel="Step Name" />
      <SubAutomationTargetFields step={step} />
      <StepAdvancedConfigFields step={step} />
    </Stack>
  );
}
